import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase/client";
import { mergePreferenceList, normalizePreferenceList, type PreferenceListChange } from "../../functions/src/preferenceMerge";
import type { UserPreferences } from "../types/domain";

type ListId = "hotelBookedWith" | "restaurantBookedWith" | "checklistCategories" | "participantRoles" | "transferModes";
interface Command { id: string; listId: ListId; change: PreferenceListChange; status: "pending" | "confirmed" | "conflict"; revision?: number }
const running = new Map<string, Promise<void>>();
const key = (uid:string) => `mundely.preferenceOutbox.v1.${uid}`;
function read(uid:string):Command[] {
  const value=localStorage.getItem(key(uid));if(value===null)return [];
  const parsed:unknown=JSON.parse(value);if(!Array.isArray(parsed))throw new Error("PREFERENCE_QUEUE_CORRUPT");
  if(parsed.some(c=>!c||typeof c!=="object"||typeof c.id!=="string"||!["hotelBookedWith","restaurantBookedWith","checklistCategories","participantRoles","transferModes"].includes(c.listId)||!["pending","confirmed","conflict"].includes(c.status)||!c.change||!Array.isArray(c.change.base)||!Array.isArray(c.change.next)))throw new Error("PREFERENCE_QUEUE_CORRUPT");
  return parsed as Command[];
}
function save(uid:string,commands:Command[]) {
  localStorage.setItem(key(uid),JSON.stringify(commands));
  window.dispatchEvent(new CustomEvent("mundely:preferences-updated",{detail:{uid}}));
}
export function preferenceOutboxStatus(uid:string):{pending:number;conflicts:number} {
  try {const commands=read(uid);return{pending:commands.filter(c=>c.status==="pending").length,conflicts:commands.filter(c=>c.status==="conflict").length};}
  catch{return{pending:0,conflicts:1};}
}
export function projectPendingPreferences(uid:string,preferences:UserPreferences):UserPreferences {
  let commands:Command[];try{commands=read(uid)}catch{return preferences;}
  const revisions=(preferences as UserPreferences & {sectionOptionRevisions?:Record<string,number>}).sectionOptionRevisions||{};
  const remaining=commands.filter(command=>!(command.status==="confirmed"&&Number(revisions[command.listId]||0)>=Number(command.revision||0)));
  if(remaining.length!==commands.length)save(uid,remaining);
  const sections={...preferences.sectionOptions};
  for(const command of remaining){if(command.status==="conflict")continue;try{sections[command.listId]=mergePreferenceList(sections[command.listId],command.change)}catch{/* The authoritative server list remains visible on conflict. */}}
  return remaining.length?{...preferences,sectionOptions:sections}:preferences;
}
export async function queuePreferenceList(uid:string,listId:ListId,change:PreferenceListChange):Promise<void> {
  if(auth.currentUser?.uid!==uid)throw new Error("PREFERENCE_ACCOUNT_CHANGED");
  if(!["hotelBookedWith","restaurantBookedWith","checklistCategories","participantRoles","transferModes"].includes(listId))throw new Error("INVALID_PREFERENCE_LIST");
  change={base:normalizePreferenceList(change.base),next:normalizePreferenceList(change.next),reset:change.reset===true};
  // Write the intent durably before any network request. New edits supersede only
  // failed/conflicting edits of this same list, never another account's queue.
  const commands=read(uid).filter(c=>c.listId!==listId||c.status!=="conflict");
  const command:Command={id:`pref_${crypto.randomUUID()}`,listId,change,status:"pending"};commands.push(command);save(uid,commands);
  if(navigator.onLine===false)return;
  await flushPreferenceOutbox(uid);
  if(read(uid).find(c=>c.id===command.id)?.status==="conflict")throw new Error("PREFERENCE_LIST_CONFLICT");
}
export async function flushPreferenceOutbox(uid:string):Promise<void> {
  if(running.has(uid))return running.get(uid)!;
  const work=(async()=>{
    while(auth.currentUser?.uid===uid&&navigator.onLine!==false){
      const command=read(uid).find(c=>c.status==="pending");if(!command)return;
      try{
        const result=await httpsCallable<{uid:string;operationId:string;listId:string;change:PreferenceListChange},{applied:boolean;revision:number}>(functions,"applyMundelyPreferenceList",{timeout:20000})({uid,operationId:command.id,listId:command.listId,change:command.change});
        const latest=read(uid);save(uid,latest.map(c=>c.id===command.id?{...c,status:"confirmed",revision:result.data.revision}:c));
      }catch(error){
        const message=String((error as {message?:unknown}).message||error);
        if(/PREFERENCE_LIST_CONFLICT|INVALID_PREFERENCE_LIST|role identities|invalid-argument/i.test(message)){
          save(uid,read(uid).map(c=>c.id===command.id?{...c,status:"conflict"}:c));continue;
        }
        return; // Offline/timeouts retain the exact operation ID for an idempotent retry.
      }
    }
  })();running.set(uid,work);try{await work}finally{running.delete(uid)}
}
